import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, Inbox, Network, Building2 } from "lucide-react";
import Reveal from "@/components/Reveal";
import CTABanner from "@/components/CTABanner";
import Chevron from "@/components/Chevron";
import { useT } from "@/i18n/LanguageProvider";

const SECTION_ICONS = [Truck, Inbox, Network, Building2];
const SECTION_IDS = ["ecommerce-retail", "smes-distributors", "enterprise-corporate", "postal-government"];

export default function Solutions() {
  const t = useT();
  const sections = (t.solutions.sections || []).map((s, i) => ({
    ...s,
    id: SECTION_IDS[i],
    icon: SECTION_ICONS[i],
  }));

  return (
    <>
      <section className="relative bg-delfi-dark text-white pt-36 pb-24 overflow-hidden" data-testid="solutions-hero">
        <div className="chevron-bg right-[-8%] top-[-18%] opacity-[0.08]" aria-hidden="true">›</div>
        <div className="absolute inset-0 grid-pattern opacity-40" aria-hidden="true" />
        <div className="delfi-container relative max-w-3xl">
          <div className="inline-flex items-center gap-2 text-delfi-red text-xs tracking-[0.2em] uppercase font-semibold">
            <Chevron size={14} /> {t.solutions.eyebrow}
          </div>
          <h1 className="headline-display text-white mt-4 text-[clamp(2.2rem,5vw,4rem)]" data-testid="solutions-title">
            {t.solutions.titleLead} <span className="text-delfi-red">{t.solutions.titleHighlight}</span>
          </h1>
          <p className="mt-6 text-white/75 text-lg leading-relaxed max-w-2xl">{t.solutions.description}</p>
        </div>
      </section>

      {sections.map((sol, idx) => {
        const Icon = sol.icon;
        const darkSection = idx === 1;
        return (
          <section
            key={sol.id}
            id={sol.id}
            className={`py-24 md:py-32 ${darkSection ? "bg-delfi-navy text-white" : idx % 2 === 0 ? "bg-white" : "bg-delfi-cloud"}`}
            data-testid={`solution-${sol.id}`}
          >
            <div className="delfi-container grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
              <Reveal>
                <div className="inline-flex items-center gap-2 text-delfi-red text-xs tracking-[0.2em] uppercase font-semibold">
                  <Chevron size={14} /> {sol.eyebrow}
                </div>
                <h2 className={`headline-section text-3xl md:text-5xl mt-4 ${darkSection ? "text-white" : "text-delfi-navy"}`}>
                  {sol.title}
                </h2>
                <p className={`mt-5 text-lg leading-relaxed ${darkSection ? "text-white/75" : "text-delfi-navy/70"}`}>
                  {sol.description}
                </p>
                <ul className="mt-7 space-y-3">
                  {sol.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="text-delfi-red mt-1 text-lg leading-none font-extrabold">›</span>
                      <span className={`${darkSection ? "text-white/85" : "text-delfi-navy/85"} text-[15px] leading-relaxed`}>
                        {b}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className={`mt-8 p-5 rounded-xl border-l-4 border-delfi-red ${darkSection ? "bg-white/5" : "bg-delfi-cloud"}`}>
                  <p className="text-sm font-semibold tracking-wide uppercase text-delfi-red">{t.solutions.typicalOutcome}</p>
                  <p className={`mt-1.5 font-medium ${darkSection ? "text-white" : "text-delfi-navy"}`}>{sol.outcome}</p>
                </div>
                <div className="mt-8">
                  <Link to="/partner" className="btn-delfi-primary" data-testid={`solution-cta-${sol.id}`}>
                    {t.common.startConversation} <ArrowRight size={18} />
                  </Link>
                </div>
              </Reveal>

              <Reveal delay={150}>
                <div className={`relative rounded-3xl p-10 overflow-hidden ${darkSection ? "bg-white/5 border border-white/10" : "bg-white delfi-card"}`}>
                  <div className="chevron-bg right-[-30%] top-[-40%] opacity-[0.08]" aria-hidden="true">›</div>
                  <div className={`w-16 h-16 rounded-2xl ${darkSection ? "bg-delfi-red/20" : "bg-delfi-red/10"} text-delfi-red flex items-center justify-center mb-6`}>
                    <Icon size={28} />
                  </div>
                  <p className={`text-xs uppercase tracking-[0.2em] font-semibold ${darkSection ? "text-white/50" : "text-delfi-navy/50"}`}>
                    {t.solutions.platformSnapshot}
                  </p>
                  <div className="mt-4 space-y-3">
                    {((sol.snapshot && sol.snapshot.length) ? sol.snapshot : (t.solutions.snapshotItems || [])).map((item, i) => (
                      <div
                        key={i}
                        className={`flex items-center gap-3 p-3 rounded-lg ${darkSection ? "bg-white/[0.03]" : "bg-delfi-cloud"}`}
                      >
                        <span className="w-8 h-8 rounded-md bg-delfi-red/15 text-delfi-red flex items-center justify-center text-sm font-bold">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className={`font-medium ${darkSection ? "text-white" : "text-delfi-navy"}`}>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </section>
        );
      })}

      <CTABanner title={t.solutions.ctaTitle} subtitle={t.solutions.ctaSubtitle} />
    </>
  );
}
